import React, {FC, useState, useCallback, useRef, useEffect} from 'react';
import {View, Pressable, TextInput, ActivityIndicator} from 'react-native';
import {signInWithCustomToken} from 'firebase/auth';
import {auth} from '@src/firebase';
import {
  useVodacomRequestOtpMutation,
  useVodacomSubmitOtpMutation,
  useLazyGetMemberByUsernameQuery,
} from '@src/Api/TraceApi/traceApi';
import {useSession} from '@src/Providers/SessionProvider/SessionProvider';
import useIsLoggedIn from '@src/Providers/AuthProvider/useIsLoggedIn';
import Typography from 'design-system/src/Components/Typography/Typography';
import Icon from 'design-system/src/Components/Icon/Icon';
import Metrics from 'design-system/src/Theme/Metrics';
import appAuthSlice from '@src/Store/Slices/AppSlice/auth.slice';
import {useAppDispatch} from '@src/Store/hooks';
import useTracker from '@src/Services/Amplitude/hooks/useTracker';
import {trackTealiumLoginSuccessful} from '@src/Tracking/tealiumEvents';
import {useTranslation} from 'gatsby-plugin-react-i18next';

const OTP_COOLDOWN_MS = 30_000;

type Step = 'msisdn' | 'otp' | 'success';

interface VodacomOtpLoginProps {
  onSuccess?: () => void;
  onBack?: () => void;
  compact?: boolean;
}

const VodacomOtpLogin: FC<VodacomOtpLoginProps> = ({
  onSuccess,
  onBack,
  compact,
}) => {
  const [step, setStep] = useState<Step>('msisdn');
  const [msisdn, setMsisdn] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [requestOtp, {isLoading: isRequestingOtp}] =
    useVodacomRequestOtpMutation();
  const [submitOtp, {isLoading: isSubmittingOtp}] =
    useVodacomSubmitOtpMutation();
  const [fetchMember] = useLazyGetMemberByUsernameQuery();

  const session = useSession();
  const {setIsLoggedIn, setSessionManuallyInitialized} = useIsLoggedIn();
  const dispatch = useAppDispatch();
  const tracker = useTracker();
  const {t} = useTranslation('auth');

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  const startCooldown = useCallback(() => {
    setCooldown(OTP_COOLDOWN_MS / 1000);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const formatMsisdn = (raw: string): string => {
    const digits = raw.replace(/\D/g, '');
    if (digits.startsWith('0')) return `27${digits.slice(1)}`;
    if (digits.startsWith('27')) return digits;
    return `27${digits}`;
  };

  const handleRequestOtp = useCallback(async () => {
    setError(null);
    const formatted = formatMsisdn(msisdn);
    if (formatted.length < 11) {
      setError(t('Please enter a valid South African phone number'));
      return;
    }

    try {
      await requestOtp({msisdn: formatted}).unwrap();
      setStep('otp');
      startCooldown();
    } catch (err: any) {
      setError(
        err?.data?.message || t('Failed to send OTP. Please try again.'),
      );
    }
  }, [msisdn, requestOtp, startCooldown]);

  const handleResendOtp = useCallback(async () => {
    if (cooldown > 0) return;
    setError(null);
    const formatted = formatMsisdn(msisdn);
    try {
      await requestOtp({msisdn: formatted}).unwrap();
      startCooldown();
    } catch (err: any) {
      setError(err?.data?.message || t('Failed to resend OTP.'));
    }
  }, [msisdn, cooldown, requestOtp, startCooldown]);

  const handleSubmitOtp = useCallback(async () => {
    setError(null);
    if (otp.length < 4) {
      setError(t('Please enter the OTP received via SMS'));
      return;
    }

    const formatted = formatMsisdn(msisdn);

    try {
      setIsVerifying(true);
      setSessionManuallyInitialized(true);
      const response = await submitOtp({msisdn: formatted, otp}).unwrap();

      const userCredential = await signInWithCustomToken(
        auth,
        response.accessToken,
      );
      if (!userCredential.user) throw new Error('Firebase auth failed');

      await userCredential.user.getIdToken(true);

      await session.initializeSession();
      await fetchMember();
      tracker.general.loggedIn({account_type: 'vodacom'});
      trackTealiumLoginSuccessful({accountType: 'vodacom'});

      setStep('success');
      setOtp('');
      setMsisdn('');
      setError(null);
      setCooldown(0);
      if (cooldownRef.current) clearInterval(cooldownRef.current);

      setTimeout(() => {
        setIsLoggedIn(true);
        dispatch(appAuthSlice.actions.closeAuthModal());
        onSuccess?.();
      }, 2000);
    } catch (err: any) {
      setIsVerifying(false);
      setError(
        err?.data?.message || t('OTP verification failed. Please try again.'),
      );
      try {
        await auth.signOut();
      } catch {}
    } finally {
      setSessionManuallyInitialized(false);
    }
  }, [
    otp,
    msisdn,
    submitOtp,
    session,
    fetchMember,
    setIsLoggedIn,
    setSessionManuallyInitialized,
    dispatch,
    tracker,
    onSuccess,
  ]);

  const isLoading = isRequestingOtp || isSubmittingOtp || isVerifying;

  return (
    <View
      style={{
        width: '100%' as any,
        alignItems: 'center',
        padding: compact ? 0 : Metrics.horizontalScale(16),
      }}>
      {onBack && step === 'msisdn' && (
        <Pressable
          onPress={onBack}
          style={{
            alignSelf: 'flex-start',
            marginBottom: Metrics.verticalScale(8),
          }}>
          <Typography variant="body2" color="primary">
            ← {t('Back')}
          </Typography>
        </Pressable>
      )}

      {step !== 'success' && (
        <>
          <Typography
            variant="body2"
            color="gray"
            style={{marginBottom: Metrics.verticalScale(8)}}>
            {t('with')}
          </Typography>
          <Icon
            name="vodacom"
            height={96}
            style={{marginBottom: Metrics.verticalScale(12)}}
          />
        </>
      )}

      {step === 'success' ? (
        <View
          style={{
            alignItems: 'center',
            paddingVertical: Metrics.verticalScale(24),
          }}>
          <Icon
            name="vodacom"
            height={120}
            style={{marginBottom: Metrics.verticalScale(16)}}
          />
          <Typography
            variant="body1"
            color="white"
            style={{textAlign: 'center', maxWidth: 320}}>
            {t('Successfully signed in with Vodacom')}
          </Typography>
        </View>
      ) : step === 'msisdn' ? (
        <>
          <Typography
            variant="body2"
            color="gray"
            style={{
              textAlign: 'center',
              marginBottom: Metrics.verticalScale(16),
              maxWidth: 320,
            }}>
            {t('Enter your Vodacom mobile number to receive a one-time PIN')}
          </Typography>

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              width: '100%' as any,
              maxWidth: 360,
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderRadius: 8,
              paddingHorizontal: Metrics.horizontalScale(12),
              height: Metrics.verticalScale(48),
              marginBottom: Metrics.verticalScale(12),
              opacity: isLoading ? 0.5 : 1,
            }}>
            <Typography
              variant="body1"
              color="gray"
              style={{marginRight: Metrics.horizontalScale(8)}}>
              +27
            </Typography>
            <TextInput
              value={msisdn}
              onChangeText={setMsisdn}
              placeholder={t('Phone number') as string}
              placeholderTextColor="rgba(255,255,255,0.3)"
              keyboardType="phone-pad"
              maxLength={15}
              editable={!isLoading}
              style={
                {
                  flex: 1,
                  color: '#fff',
                  fontSize: 16,
                  outlineStyle: 'none',
                } as any
              }
              onSubmitEditing={handleRequestOtp}
            />
          </View>

          <Pressable
            onPress={handleRequestOtp}
            disabled={isLoading || !msisdn.trim()}
            style={{
              backgroundColor:
                isLoading || !msisdn.trim() ? 'rgba(230,0,0,0.4)' : '#E60000',
              width: '100%' as any,
              maxWidth: 360,
              paddingVertical: Metrics.verticalScale(14),
              borderRadius: 8,
              alignItems: 'center',
            }}>
            {isRequestingOtp ? (
              <View style={{flexDirection: 'row', alignItems: 'center'}}>
                <ActivityIndicator
                  size="small"
                  color="#fff"
                  style={{marginRight: Metrics.horizontalScale(8)}}
                />
                <Typography variant="button1" color="white">
                  {t('Sending...')}
                </Typography>
              </View>
            ) : (
              <Typography variant="button1" color="white">
                {t('Send OTP')}
              </Typography>
            )}
          </Pressable>
        </>
      ) : (
        <>
          <Typography
            variant="body2"
            color="gray"
            style={{
              textAlign: 'center',
              marginBottom: Metrics.verticalScale(16),
              maxWidth: 320,
            }}>
            {t('Enter the OTP sent to')} +27{formatMsisdn(msisdn).slice(2)}
          </Typography>

          <View
            style={{
              width: '100%' as any,
              maxWidth: 360,
              backgroundColor: 'rgba(255,255,255,0.08)',
              borderRadius: 8,
              paddingHorizontal: Metrics.horizontalScale(12),
              height: Metrics.verticalScale(48),
              justifyContent: 'center',
              marginBottom: Metrics.verticalScale(12),
              opacity: isLoading ? 0.5 : 1,
            }}>
            <TextInput
              value={otp}
              onChangeText={setOtp}
              placeholder={t('Enter OTP') as string}
              placeholderTextColor="rgba(255,255,255,0.3)"
              keyboardType="number-pad"
              maxLength={6}
              editable={!isLoading}
              style={
                {
                  color: '#fff',
                  fontSize: 20,
                  textAlign: 'center',
                  letterSpacing: 8,
                  outlineStyle: 'none',
                } as any
              }
              onSubmitEditing={handleSubmitOtp}
            />
          </View>

          <Pressable
            onPress={handleSubmitOtp}
            disabled={isLoading || otp.length < 4}
            style={{
              backgroundColor:
                isLoading || otp.length < 4 ? 'rgba(230,0,0,0.4)' : '#E60000',
              width: '100%' as any,
              maxWidth: 360,
              paddingVertical: Metrics.verticalScale(14),
              borderRadius: 8,
              alignItems: 'center',
              marginBottom: Metrics.verticalScale(12),
            }}>
            {isVerifying || isSubmittingOtp ? (
              <View style={{flexDirection: 'row', alignItems: 'center'}}>
                <ActivityIndicator
                  size="small"
                  color="#fff"
                  style={{marginRight: Metrics.horizontalScale(8)}}
                />
                <Typography variant="button1" color="white">
                  {t('Verifying...')}
                </Typography>
              </View>
            ) : (
              <Typography variant="button1" color="white">
                {t('Verify')}
              </Typography>
            )}
          </Pressable>

          <Pressable
            onPress={handleResendOtp}
            disabled={cooldown > 0 || isLoading}>
            <Typography
              variant="body2"
              color={cooldown > 0 ? 'gray' : 'primary'}>
              {cooldown > 0
                ? t('Resend OTP in {{seconds}}s', {seconds: cooldown})
                : t('Resend OTP')}
            </Typography>
          </Pressable>

          {!isLoading && (
            <Pressable
              onPress={() => {
                setStep('msisdn');
                setOtp('');
                setError(null);
              }}
              style={{marginTop: Metrics.verticalScale(8)}}>
              <Typography variant="body2" color="gray">
                {t('Change number')}
              </Typography>
            </Pressable>
          )}
        </>
      )}

      {error && (
        <Typography
          variant="body2"
          color="error"
          style={{
            textAlign: 'center',
            marginTop: Metrics.verticalScale(12),
            maxWidth: 320,
          }}>
          {error}
        </Typography>
      )}
    </View>
  );
};

export default VodacomOtpLogin;
